import React, { RefObject, useEffect, useState } from 'react';
import { CommentPopover, COMMENT_POPPER_WIDTH } from '../CommentPopover/CommentPopover.component';
import { CommentTypeId, IComment } from '../Comments/Comments.types';
import {
  commentImageData,
  commentImageDataWithPopperPosition,
  Coords,
  pageImageData,
  Point,
  PreviewAction,
  PreviewActionType,
  rotateAngleValue,
} from './Preview.types';
import useSessionUser from '../../hooks/useSessionUser';
import useLongClick from '../../hooks/useLongClick';
import {
  translatePagePositionToCanvasCoordinates,
  getPointRelativeToImage,
  getImageRotateOffsetForIcons,
  getTargetPageImage,
  makeImageLocked,
  makeImageMovable,
} from './previewUtils';
import { updateComment } from '../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { fabric } from 'fabric';
import { OnPageCommentIcon } from '../Comments/icons';
import { OnPageCommentIconHighContrast } from '../Comments/icons';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { highContrastMode } from '../../utils/highContrastChecker';

const COMMENT_ICON_SIZE = 40;
const COMMENT_OBJECT_NAME = 'komentarz';

interface CommentsOnPageManagerProps {
  canvas: fabric.Canvas;
  canvasZoom: number;
  comments: IComment[];
  orgzId?: string;
  pageImages: pageImageData[];

  commentsOnPageVisible: boolean;
  allCommentsOnPageVisible: boolean;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  canEditComment: boolean;
  rotateAngle: rotateAngleValue;
  examinerFunction: ExaminerFunction;

  actionsToTrigger: PreviewAction[];
  onActionTrigger: (action: PreviewAction) => void;
  onCommentChanged: (comment: IComment | null) => void;

  getPopoverCoord: (left: number, top: number, zoom: number, offset: Coords) => Point;
  getOffsetForPopovers: () => Coords;
  getCanvasPoint: (clientX: number, clientY: number) => Point;
}

const CommentsOnPageManager: React.FC<CommentsOnPageManagerProps> = (props) => {
  const { user } = useSessionUser();
  const { openAlert } = useLayoutContext();
  const { beginLongClick, cancelLongClick } = useLongClick();

  const [commentImages, setCommentImages] = useState<commentImageData[]>([]);
  const [commentsWithPopperCoords, setCommentsWithPopperCoords] = useState<commentImageDataWithPopperPosition[]>([]);
  const [closedCommentsIds, setClosedCommentsIds] = useState<string[]>([]);
  const [activeOnPageComment, setActiveOnPageComment] = useState<commentImageDataWithPopperPosition | undefined>(
    undefined,
  );
  const [commentsToDisplay, setCommentsToDisplay] = useState<IComment[] | undefined>(props.comments);
  const [newComment, setNewComment] = useState<IComment | undefined>(undefined);

  const getCurrentCommentIconScale = (zoom: number): number => {
    return COMMENT_ICON_SIZE / 33 / zoom;
  };

  const clearCommentImages = (): void => {
    if (commentImages && commentImages.length) {
      commentImages.forEach((obj: commentImageData) => {
        props.canvas.remove(obj.image);
      });
      setCommentImages([]);
    }

    props.canvas.getObjects().forEach((item) => {
      if (item.name === COMMENT_OBJECT_NAME) {
        props.canvas.remove(item);
      }
    });
  };

  const rotateCommentImages = (pageImageCoords: Coords) => {
    commentImages.forEach((commentImage: commentImageData) => {
      if (commentImage.comment.x !== null && commentImage.comment.y !== null) {
        const { leftOffset, topOffset } = getImageRotateOffsetForIcons(
          props.rotateAngle,
          commentImage.comment.x,
          commentImage.comment.y,
        );

        commentImage.image.set({
          left: pageImageCoords.left + leftOffset,
          top: pageImageCoords.top + topOffset,
        });
      }
    });
  };

  const updateCommentPopperPosition = () => {
    const zoom = props.canvas.getZoom();
    const offset = props.getOffsetForPopovers();

    if (props.allCommentsOnPageVisible) {
      const commentsToOpen: commentImageDataWithPopperPosition[] = commentImages.map((item: commentImageData) => {
        return {
          image: item.image,
          comment: item.comment,
          ...props.getPopoverCoord(item.image.left ?? 0, item.image.top ?? 0, zoom, offset),
        };
      });
      setCommentsWithPopperCoords(commentsToOpen);
    }
    if (activeOnPageComment || commentsWithPopperCoords.length) {
      if (activeOnPageComment && activeOnPageComment.image.isOnScreen()) {
        setActiveOnPageComment((prevState: commentImageDataWithPopperPosition | undefined) => {
          if (!prevState) {
            return prevState;
          } else
            return {
              ...prevState,
              ...props.getPopoverCoord(
                activeOnPageComment.image.left ?? 0,
                activeOnPageComment.image.top ?? 0,
                zoom,
                offset,
              ),
            };
        });
      }

      setCommentsWithPopperCoords((prevState) => {
        return prevState.map((item: commentImageDataWithPopperPosition) => {
          return {
            ...item,
            ...props.getPopoverCoord(item.image.left ?? 0, item.image.top ?? 0, zoom, offset),
          };
        });
      });
    }
  };

  const handleCancelComment = () => {
    setActiveOnPageComment(undefined);
    if (newComment) {
      setCommentsToDisplay((prevState) => prevState?.filter((item: IComment) => item !== newComment));
      const newCommentData = commentImages.find((obj: commentImageData) => obj.comment === newComment);
      if (newCommentData) {
        props.canvas.remove(newCommentData.image);
      }
      setNewComment(undefined);
    }
  };

  const handleDropNewComment = (x: number, y: number) => {
    if (activeOnPageComment) {
      handleCancelComment();
    }
    const canvasPoints = props.getCanvasPoint(x, y);
    const targetImageData: pageImageData | undefined = getTargetPageImage(
      props.pageImages,
      canvasPoints.x,
      canvasPoints.y,
    );
    if (
      targetImageData &&
      targetImageData.image &&
      targetImageData.image.left !== undefined &&
      targetImageData.image.top !== undefined
    ) {
      const coords = getPointRelativeToImage(canvasPoints.x, canvasPoints.y, targetImageData);
      const newComment = {
        commentId: '',
        examinerId: user.profile.sub,
        examinerFirstName: user.profile.given_name ?? '',
        examinerLastName: user.profile.family_name ?? '',
        examinerFunction: props.examinerFunction,
        commentTypeId: CommentTypeId.ONIMAGE,
        history: [],
        content: '',
        x: coords.x,
        y: coords.y,
        pageNumber: targetImageData.pageNumber,
      };
      setNewComment(newComment);
      setCommentsToDisplay((prevState) => {
        return prevState ? [...prevState, newComment] : [newComment];
      });
    }
  };

  const openCommentPopover = (image: fabric.Object, comment: IComment) => {
    if (props.allCommentsOnPageVisible) {
      setClosedCommentsIds((prevState) => {
        return prevState.filter((id) => id !== comment.commentId);
      });
    } else {
      const zoom = props.canvas.getZoom();
      const offset = props.getOffsetForPopovers();
      setActiveOnPageComment({
        image,
        comment,
        ...props.getPopoverCoord(image.left ?? 0, image.top ?? 0, zoom, offset),
      });
    }
  };

  const updateMovedComment = (image: fabric.Object, comment: IComment) => {
    if (image.left !== undefined && image.top !== undefined && image.height !== undefined) {
      const targetImageData: pageImageData | undefined = getTargetPageImage(props.pageImages, image.left, image.top);
      if (targetImageData) {
        const commentCoords = getPointRelativeToImage(image.left, image.top, targetImageData);
        if (
          commentCoords.x !== comment.x ||
          commentCoords.y !== comment.y ||
          targetImageData.pageNumber !== comment.pageNumber
        ) {
          updateComment(
            comment.commentId,
            comment.content,
            commentCoords.x,
            commentCoords.y,
            targetImageData.pageNumber,
          ).then(
            (response: AxiosResponse<boolean>) => {
              if (response.data) {
                setCommentsToDisplay((prevState) => {
                  return prevState?.map((item: IComment) => {
                    return item.commentId === comment.commentId
                      ? {
                          ...item,
                          x: commentCoords.x,
                          y: commentCoords.y,
                          pageNumber: targetImageData.pageNumber,
                        }
                      : item;
                  });
                });
                props.onCommentChanged(comment);
              } else {
                openAlert('save_error');
              }
            },
            () => {
              openAlert('save_error');
            },
          );
        }
      }
    }
  };

  const addCommentIcon = (source: fabric.Object, left: number, top: number, comment: IComment) => {
    source.clone((image: fabric.Object) => {
      image.set({
        left,
        top,
        originX: 'left',
        originY: 'bottom',
        hasControls: false,
        borderColor: '#3560FF',
        borderOpacityWhenMoving: 1,
        selectable: true,
      });
      image.name = COMMENT_OBJECT_NAME;
      makeImageLocked(image);
      image.scale(getCurrentCommentIconScale(props.canvas.getZoom()));
      const newCommentImageData = { image, comment };
      setCommentImages((prevState: commentImageData[]) => {
        return [...prevState, newCommentImageData];
      });

      const currentUserIsAuthor = comment.examinerId === user.profile.sub;

      image.on('mousedown', (event: fabric.IEvent) => {
        if (currentUserIsAuthor) {
          event.e.preventDefault();
          event.e.stopPropagation();
          beginLongClick(() => {
            makeImageMovable(image);
            props.canvas.renderAll();
          });
        }
      });

      image.on('mouseup', () => {
        cancelLongClick();
        if (image.lockMovementX) {
          // just normal click not long one
          openCommentPopover(image, comment);
        } else {
          // long click with d'n'd
          updateMovedComment(image, comment);
        }
        makeImageLocked(image);
      });

      image.on('mousemove', () => {
        cancelLongClick();
      });

      if (newComment) {
        const zoom = props.canvas.getZoom();
        const offset = props.getOffsetForPopovers();
        setActiveOnPageComment({
          image,
          comment,
          ...props.getPopoverCoord(image.left ?? 0, image.top ?? 0, zoom, offset),
        });
      }
      props.canvas.add(image);
      image.bringToFront();
    });
  };

  const handleDragOverComment = (e: React.DragEvent<HTMLDivElement>) => {
    const targetImage = props.canvas.findTarget(e as unknown as Event, true);
    e.dataTransfer.dropEffect = targetImage ? 'move' : 'none';
  };

  const handleCloseOneOfOpenedComment = (commentId: string) => {
    setClosedCommentsIds((prevState) => [...prevState, commentId]);
    ``;
  };

  const handleRemoveComment = (comment: IComment) => {
    props.onCommentChanged && props.onCommentChanged(comment);
    const deletedCommentData = commentImages.find(
      (obj: commentImageData) => obj.comment.commentId === comment.commentId,
    );
    if (deletedCommentData) {
      props.canvas.remove(deletedCommentData.image);
    }
    setActiveOnPageComment(undefined);
    setCommentsToDisplay((prevState) => prevState?.filter((item: IComment) => item.commentId !== comment.commentId));
  };

  const handleSaveComment = (comment: IComment, content: string) => {
    props.onCommentChanged && props.onCommentChanged(comment);

    if (newComment) {
      setNewComment(undefined);
      setActiveOnPageComment(undefined);
    }
    if (!newComment && props.orgzId && activeOnPageComment) {
      setActiveOnPageComment((prevState: commentImageDataWithPopperPosition | undefined) => {
        if (!prevState) {
          return prevState;
        } else
          return {
            ...prevState,
            comment: { ...comment, content },
          };
      });
    }
  };

  const getCommentPopperPlacement = (commentData: commentImageDataWithPopperPosition): 'left' | 'right' => {
    return props.canvasContainerRef?.current &&
      commentData.x + COMMENT_POPPER_WIDTH > props.canvasContainerRef.current.clientWidth
      ? 'left'
      : 'right';
  };

  useEffect(() => {
    if (!props.orgzId || !props.pageImages.length) {
      return;
    }

    clearCommentImages();
    if (!commentsToDisplay?.length) {
      return;
    }
    if (props.commentsOnPageVisible) {
      fabric.loadSVGFromString(
        highContrastMode ? OnPageCommentIconHighContrast : OnPageCommentIcon,
        function (objects, options) {
          const source = fabric.util.groupSVGElements(objects, options);
          clearCommentImages();
          if (commentsToDisplay.length) {
            commentsToDisplay.forEach((comment: IComment) => {
              if (comment.x === null || comment.y == null) {
                return;
              }
              const commentCoordinates = translatePagePositionToCanvasCoordinates(
                props.pageImages,
                comment.x,
                comment.y,
                comment.pageNumber,
              );
              if (commentCoordinates) {
                addCommentIcon(source, commentCoordinates.left, commentCoordinates.top, comment);
              }
            });
          }
        },
      );
    } else clearCommentImages();
  }, [props.pageImages, commentsToDisplay, props.commentsOnPageVisible, props.allCommentsOnPageVisible, props.orgzId]);

  useEffect(() => {
    setCommentsToDisplay(props.comments);
  }, [props.comments]);

  useEffect(() => {
    if (!props.allCommentsOnPageVisible) {
      if (commentsWithPopperCoords.length) {
        setCommentsWithPopperCoords([]);
      }
      if (closedCommentsIds.length) {
        setClosedCommentsIds([]);
      }
    }
  }, [props.allCommentsOnPageVisible, commentsWithPopperCoords, closedCommentsIds]);

  useEffect(() => {
    const iconScale = getCurrentCommentIconScale(props.canvasZoom);
    commentImages.forEach((obj: commentImageData) => {
      obj.image.scale(iconScale);
    });
  }, [props.canvasZoom]);

  useEffect(() => {
    props.actionsToTrigger.forEach((action) => {
      switch (action.type) {
        case PreviewActionType.clearComments: {
          clearCommentImages();
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.rotateComments: {
          rotateCommentImages(action.payload as Coords);
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.moveCommentsPopovers: {
          updateCommentPopperPosition();
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.dropComment: {
          const point = action.payload as Point;
          handleDropNewComment(point.x, point.y);
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.dragOverComment: {
          const event = action.payload as React.DragEvent<HTMLDivElement>;
          handleDragOverComment(event);
          props.onActionTrigger(action);
          break;
        }
      }
    });
  }, [props.actionsToTrigger]);

  return (
    <>
      {activeOnPageComment && props.orgzId && (
        <CommentPopover
          orgzId={props.orgzId}
          comment={activeOnPageComment.comment}
          anchorCoords={{
            x: activeOnPageComment.x,
            y: activeOnPageComment.y,
            width: COMMENT_ICON_SIZE,
            height: COMMENT_ICON_SIZE,
          }}
          onClose={handleCancelComment}
          onCancel={handleCancelComment}
          onSave={handleSaveComment}
          onRemove={handleRemoveComment}
          mode={!!newComment ? 'create' : 'view'}
          readOnly={!props.canEditComment}
          placement={getCommentPopperPlacement(activeOnPageComment)}
        />
      )}

      {props.commentsOnPageVisible &&
        commentsWithPopperCoords.map((item: commentImageDataWithPopperPosition) => {
          if (
            props.orgzId &&
            !closedCommentsIds.find((id: string) => id === item.comment.commentId) &&
            item.comment !== newComment
          ) {
            return (
              <CommentPopover
                key={item.comment.commentId}
                orgzId={props.orgzId}
                comment={item.comment}
                anchorCoords={{
                  x: item.x,
                  y: item.y,
                  width: COMMENT_ICON_SIZE,
                  height: COMMENT_ICON_SIZE,
                }}
                onClose={() => handleCloseOneOfOpenedComment(item.comment.commentId)}
                onCancel={handleCancelComment}
                onSave={handleSaveComment}
                onRemove={handleRemoveComment}
                mode="view"
                avoidAwayClosing={true}
                readOnly={!props.canEditComment}
                placement={getCommentPopperPlacement(item)}
              />
            );
          }
        })}
    </>
  );
};

export default CommentsOnPageManager;
